import { PopoverPlacement } from '@idui/react-popover';
import classNames from 'classnames';
import { ConversationPrompts } from 'utils/gloo-types';
import { useIsMobile } from 'utils/hooks';
import Icon from 'components/atoms/Icon';
import HelpPopover from 'components/molecules/HelpPopover';
import Typography from 'components/atoms/Typography';
import CloudinaryVideoPlayer from 'components/organisms/CloudinaryVideoPlayer';

interface ConversationPromptCardProps {
  prompt: Partial<ConversationPrompts>;
  highlightedTopicIds?: number[];
  onInsertMessage?: (text) => void;
  isInPreview?: boolean;
}

const ConversationPromptCard = ({
  prompt,
  highlightedTopicIds = [],
  onInsertMessage,
  isInPreview = false,
}: ConversationPromptCardProps) => {
  const isMobile = useIsMobile();

  return (
    <div
      key={`message-${prompt.id}`}
      className={
        (classNames(
          'card shadow-lg hover:shadow-xl group rounded-lg bg-white flex flex-1 flex-col my-6 justify-between',
        ),
        isMobile && isInPreview ? '' : 'p-4 ml-4')
      }
    >
      <div className="mb-4 flex items-center justify-between">
        <div className="flex items-center">
          <HelpPopover
            content={<div className="w-24">Author</div>}
            placement={PopoverPlacement.top}
            popoverClassName="w-24"
            className="mr-1"
          >
            <Icon name="edit-2" className="text-glooNavy-700" size={12} />
          </HelpPopover>
          {prompt.authorImageUrl && (
            <img src={prompt.authorImageUrl} alt={prompt.author} className="bg-white w-5 h-5 rounded-full mr-2" />
          )}
          <Typography variant="xs" className="text-neutral-600">
            {prompt.author}
          </Typography>
        </div>
        {onInsertMessage && (
          <div className="self-end">
            <button
              type="button"
              data-durable-id="insertPromptIntoConversation"
              data-intercom-target="insertPromptIntoConversation"
              onClick={() => onInsertMessage(prompt.id)}
            >
              <Icon name="plus-circle" className="text-neutral-600" size={18} />
            </button>
          </div>
        )}
      </div>
      <div
        className="bg-glooNavy-600 duration-300 text-white text-sm p-4 rounded-md rounded-br-none"
        style={{ overflowWrap: 'break-word' }}
      >
        {!!prompt.messageMedia?.length && <img src={prompt.messageMedia?.[0].url} alt={prompt.name} />}
        {prompt.message}
      </div>
      {prompt.promptTip && prompt.promptTip.length > 0 && (
        <div className="mt-4 mb-2 text-sm text-neutral-700">
          <span className="font-bold">TIP:</span> {prompt.promptTip}
        </div>
      )}
      {prompt.videoUrl && (
        <div className="my-2">
          <Typography variant="sm" className="mb-1">
            <strong>Attached Video</strong> (this gets attached as a shortened URL when sending out the message)
          </Typography>
          <CloudinaryVideoPlayer
            config={{ file: { attributes: { preload: 'none' } } }}
            url={prompt.videoUrl}
            wrapClassName="max-h-48 m-auto"
          />
        </div>
      )}
      <div className={`flex flex-wrap ${!prompt.promptTip ? 'py-3' : 'py-0'} gap-y-2`}>
        {prompt?.topicsArr?.map((topic) => {
          const highlightedClass = highlightedTopicIds.includes(topic.id)
            ? 'bg-glooNavy-700 text-white'
            : 'bg-stormBlue-100 text-glooNavy-900';
          return (
            <div
              key={topic.value}
              className={`${highlightedClass} mr-1 text-xs font-bold inline-block py-1 px-2 rounded-lg align-middle select-none`}
            >
              {topic.title}
            </div>
          );
        })}
      </div>
    </div>
  );
};

export default ConversationPromptCard;
